import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { fmtDate, isoDateInput, inr } from "../../lib/format";
import { ArrowLeft } from "lucide-react";

export default function StrategyDetail() {
  const { id } = useParams();
  const [strat, setStrat] = useState(null);
  const [positions, setPositions] = useState([]);
  const [recForm, setRecForm] = useState({
    company: "", symbol: "", industry: "", close_price: "", effective_date: isoDateInput(),
  });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => {
    api.get(`/strategies/${id}/stats`).then((r) => setStrat(r.data)).catch(() => setStrat({ error: true }));
    api.get(`/ra/positions`, { params: { strategy_id: id } }).then((r) => setPositions(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  if (!strat) return <div className="text-zinc-500">Loading...</div>;
  if (strat.error) return <div className="text-loss">Strategy not found.</div>;

  const stats = strat.stats || {};
  const livePositions = positions.filter((p) => !p.sell_date && p.buy_date);
  const exitedPositions = positions.filter((p) => p.sell_date);

  const submitRec = async (e) => {
    e.preventDefault();
    if (!strat.is_active) { toast.error("Activate the strategy first"); return; }
    try {
      await api.post("/ra/recommendations", {
        strategy_id: id,
        action: "buy",
        company: recForm.company,
        symbol: recForm.symbol,
        industry: recForm.industry,
        close_price: Number(recForm.close_price),
        effective_date: recForm.effective_date || undefined,
      });
      toast.success(`BUY ${recForm.symbol} added to '${strat.name}' · engine firing in background`);
      setRecForm({ company: "", symbol: "", industry: "", close_price: "", effective_date: isoDateInput() });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitSell = async (pos) => {
    const price = window.prompt(`Sell rate for ${pos.symbol} (₹):`, pos.current_rate ? pos.current_rate.toFixed(2) : "");
    if (price === null) return;
    const close_price = Number(price);
    if (!close_price) { toast.error("Invalid price"); return; }
    try {
      await api.post("/ra/recommendations", {
        strategy_id: id,
        action: "sell",
        company: pos.company || pos.symbol,
        symbol: pos.symbol,
        industry: pos.industry || "—",
        close_price,
        effective_date: isoDateInput(),
      });
      toast.success(`SELL ${pos.symbol} recorded at ₹${close_price.toFixed(2)} · engine firing in background`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitPaperClose = async (pos) => {
    const prefill = pos.current_rate ? pos.current_rate.toFixed(2) : "";
    const price = window.prompt(`Paper-close ${pos.symbol} — Sell rate (₹):\n\nThis pending BUY was never allotted to any client portfolio. Closing it will record the trade in the strategy P&L only.`, prefill);
    if (price === null) return;
    const sell_price = Number(price);
    if (!sell_price) { toast.error("Invalid price"); return; }
    try {
      await api.post(`/ra/recommendations/${pos.rec_id}/paper-close`, { sell_price });
      toast.success(`Paper-closed ${pos.symbol} @ ₹${sell_price.toFixed(2)} — strategy P&L updated`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "Failed to paper-close"));
    }
  };

  const submitDelete = async (pos) => {
    if (!window.confirm(`Delete PENDING ${pos.buy_status === "Pending" ? "BUY" : "recommendation"} for ${pos.symbol}?\n\nThis is only possible for recommendations that were never processed by the engine (no client holdings created).`)) return;
    try {
      const r = await api.delete(`/ra/recommendations/${pos.rec_id}`);
      toast.success(`Deleted pending ${pos.symbol} rec (${r.data.queue_rows_removed} queue rows cleared)`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "Failed to delete"));
    }
  };

  const uploadBulk = async (e) => {
    e.preventDefault();
    if (!bulkFile) { toast.error("Pick a CSV"); return; }
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      fd.append("strategy_id", id);
      const r = await api.post("/ra/recommendations/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      toast.success(`${r.data.rows_inserted} rows inserted · engine firing in background`);
      setBulkFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const pnlClass = stats.pnl > 0 ? "text-profit" : stats.pnl < 0 ? "text-loss" : "text-zinc-700";

  return (
    <div className="space-y-6">
      <div>
        <Link to="/ra/strategies" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
          <ArrowLeft size={12} /> All strategies
        </Link>
        <div className="flex items-start justify-between mt-1">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Strategy</div>
            <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">{strat.name}</h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{strat.description || "No description."}</p>
          </div>
          <Badge className={strat.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
            {strat.is_active ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Live stocks" value={stats.live_stocks ?? "—"} />
        <StatTile label="Exited stocks" value={stats.exited_stocks ?? "—"} />
        <StatTile label="PnL (₹)" value={inr(stats.pnl || 0, { decimals: 0 })} cls={pnlClass} />
        <StatTile label="% PnL" value={`${(stats.pnl_pct ?? 0).toFixed(2)}%`} cls={pnlClass} sub={`${stats.total_buys || 0} BUY rec(s) @ ₹10,000 notional`} />
      </div>

      {strat.is_active && (
        <Card className="border-zinc-200 shadow-none">
          <CardContent className="p-6">
            <h3 className="font-display font-semibold text-zinc-950 mb-4">Add BUY recommendation</h3>
            <form onSubmit={submitRec} className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div><Label>Company</Label><Input data-testid="sd-company" required value={recForm.company} onChange={(e) => setRecForm({...recForm, company: e.target.value})} className="mt-1" /></div>
              <div><Label>Symbol</Label><Input data-testid="sd-symbol" required value={recForm.symbol} onChange={(e) => setRecForm({...recForm, symbol: e.target.value.toUpperCase()})} className="mt-1 font-mono" /></div>
              <div><Label>Industry</Label><Input data-testid="sd-industry" required value={recForm.industry} onChange={(e) => setRecForm({...recForm, industry: e.target.value})} className="mt-1" /></div>
              <div><Label>Buy rate (₹)</Label><Input data-testid="sd-rate" required type="number" step="0.01" value={recForm.close_price} onChange={(e) => setRecForm({...recForm, close_price: e.target.value})} className="mt-1 font-mono" /></div>
              <div><Label>Effective from</Label><Input required type="date" value={recForm.effective_date} onChange={(e) => setRecForm({...recForm, effective_date: e.target.value})} className="mt-1" /></div>
              <div className="flex items-end">
                <Button data-testid="sd-add-rec" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white w-full">Add</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-6">
          <h3 className="font-display font-semibold text-zinc-950 mb-3">Bulk upload — recommendations</h3>
          <p className="text-xs text-zinc-500 mb-3">CSV: <span className="font-mono">trade_date, close, action, company, symbol, industry</span>. Strategy is fixed to <span className="font-semibold">{strat.name}</span>.</p>
          <form onSubmit={uploadBulk} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2"><Label>CSV file</Label><Input data-testid="sd-bulk-file" type="file" accept=".csv" required onChange={(e) => setBulkFile(e.target.files[0])} className="mt-1" /></div>
            <Button data-testid="sd-bulk-btn" type="submit" disabled={bulkBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white">{bulkBusy ? "Uploading..." : "Bulk Upload"}</Button>
          </form>
          {bulkResult && <div className="mt-3 text-xs text-zinc-600 font-mono">Inserted: {bulkResult.rows_inserted} rows. {bulkResult.issues?.length ? `${bulkResult.issues.length} issue(s).` : ""}</div>}
        </CardContent>
      </Card>

      <PositionsTable title="Live positions" rows={livePositions} stats={stats} onSell={submitSell} onPaperClose={submitPaperClose} canSell={strat.is_active} />
      <PositionsTable title="Exited positions" rows={exitedPositions} stats={stats} onSell={() => {}} onPaperClose={() => {}} canSell={false} exited />
    </div>
  );
}

function StatTile({ label, value, cls = "text-zinc-950", sub }) {
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
        <div className={`font-mono text-2xl mt-1 ${cls}`}>{value}</div>
        {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function PositionsTable({ title, rows, onSell, onPaperClose, canSell, exited }) {
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-0">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="font-display font-semibold text-zinc-950">{title}</div>
          <Badge className="bg-zinc-50 text-zinc-700 border-zinc-200">{rows.length}</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right w-12">No.</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead className="text-right">Buy Date</TableHead>
              <TableHead className="text-right">Buy Rate</TableHead>
              {exited ? <TableHead className="text-right">Sell Date</TableHead> : <TableHead className="text-right">Current Date</TableHead>}
              {exited ? <TableHead className="text-right">Sell Rate</TableHead> : <TableHead className="text-right">Current Rate</TableHead>}
              <TableHead className="text-right">% P&amp;L</TableHead>
              {canSell && <TableHead className="text-right w-28">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={canSell ? 9 : 8} className="text-zinc-500">No {title.toLowerCase()}.</TableCell></TableRow>}
            {rows.map((p, idx) => {
              const pnl = p.pnl_pct;
              const pnlClass = pnl == null ? "text-zinc-500" : pnl > 0 ? "text-profit" : pnl < 0 ? "text-loss" : "text-zinc-700";
              const isPending = p.buy_status === "Pending";
              return (
                <TableRow key={p.rec_id} className={isPending ? "bg-amber-50/50" : ""}>
                  <TableCell className="text-right font-mono text-xs text-zinc-500">{idx + 1}</TableCell>
                  <TableCell className="font-mono font-semibold">
                    {p.symbol}
                    {isPending && <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1.5 py-0" data-testid={`sd-pending-badge-${p.symbol}`}>PENDING</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">{p.industry}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtDate(p.buy_date) || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{p.buy_rate != null ? inr(p.buy_rate, { decimals: 2 }) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtDate(exited ? p.sell_date : p.current_date) || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{(exited ? p.sell_rate : p.current_rate) != null ? inr(exited ? p.sell_rate : p.current_rate, { decimals: 2 }) : "—"}</TableCell>
                  <TableCell className={`text-right font-mono ${pnlClass}`}>{pnl == null ? "—" : pnl.toFixed(2) + "%"}</TableCell>
                  {canSell && (
                    <TableCell className="text-right">
                      {isPending ? (
                        <Button data-testid={`sd-sell-${p.symbol}`} size="sm" variant="outline" onClick={() => onPaperClose(p)} className="h-7 text-xs">Sell</Button>
                      ) : (
                        <Button data-testid={`sd-sell-${p.symbol}`} size="sm" variant="outline" onClick={() => onSell(p)} className="h-7 text-xs">Sell</Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
